import { useCallback, useEffect, useRef, useState } from 'react';

import { useWebSocket } from '../contexts/WebSocketContext';


export interface UserActivityReportObserverEvent {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  name?: string | null;
  description?: string | null;
  created_by_name?: string | null;
  file_url?: string | null;
  file_name?: string | null;
  file_size?: number | null;
  total_events?: number;
  created_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  error_message?: string | null;
  progress?: number;
  processed_events?: number;
  estimated_seconds_remaining?: number | null;
  progress_message?: string | null;
  filters_metadata?: Record<string, unknown>;
}

interface UseUserActivityReportWebSocketOptions {
  onReportUpdated?: (event: UserActivityReportObserverEvent) => void;
  onReportReady?: (event: UserActivityReportObserverEvent) => void;
  onReportFailed?: (event: UserActivityReportObserverEvent) => void;
}

interface UseUserActivityReportWebSocketReturn {
  isConnected: boolean;
  isSubscribed: boolean;
}

export function useUserActivityReportWebSocket(
  reportId: string | undefined,
  options: UseUserActivityReportWebSocketOptions = {},
): UseUserActivityReportWebSocketReturn {
  const { onReportUpdated, onReportReady, onReportFailed } = options;
  const { state, addObserver, removeObserver } = useWebSocket();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const callbacksRef = useRef<UseUserActivityReportWebSocketOptions>({
    onReportUpdated,
    onReportReady,
    onReportFailed,
  });

  useEffect(() => {
    callbacksRef.current = {
      onReportUpdated,
      onReportReady,
      onReportFailed,
    };
  }, [onReportUpdated, onReportReady, onReportFailed]);

  const handleEvent = useCallback(
    (data: UserActivityReportObserverEvent, action: string) => {
      if (action !== 'update' && action !== 'create') {
        return;
      }
      if (reportId && data.id && data.id !== reportId) {
        return;
      }

      callbacksRef.current.onReportUpdated?.(data);
      if (data.status === 'completed') {
        callbacksRef.current.onReportReady?.(data);
      } else if (data.status === 'failed') {
        callbacksRef.current.onReportFailed?.(data);
      }
    },
    [reportId],
  );

  useEffect(() => {
    if (!reportId || !state.isConnected) {
      setIsSubscribed(false);
      return;
    }

    const observerId = addObserver(
      'user_activity_report',
      { data: { report_id: reportId } },
      handleEvent,
      {
        subscribeAction: 'subscribe',
        unsubscribeAction: 'unsubscribe',
        includeCreateEvents: true,
      },
    );
    setIsSubscribed(true);

    return () => {
      removeObserver(observerId);
      setIsSubscribed(false);
    };
  }, [reportId, state.isConnected, addObserver, removeObserver, handleEvent]);

  return {
    isConnected: state.isConnected,
    isSubscribed,
  };
}

export default useUserActivityReportWebSocket;
